import * as React from 'react';
import { ExistingAccountSummary } from '@signup/shared';

interface OidcExistingAccountsPickerProps {
  email: string;
  existingAccounts: ExistingAccountSummary[];
  onSelectExisting: (account: ExistingAccountSummary) => void;
  onCreateNew: () => void;
}

function formatCreatedAt(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoString;
  }
}

export const OidcExistingAccountsPicker: React.FC<OidcExistingAccountsPickerProps> = ({
  email,
  existingAccounts,
  onSelectExisting,
  onCreateNew,
}) => {
  const count = existingAccounts.length;

  return (
    <div className="oidc-picker-card">
      <div className="oidc-picker-header">
        <img src="/static/images/logo-sno-blue.svg" alt="Snowflake" className="oidc-picker-logo" />
        <h2 className="oidc-picker-title">Welcome back!</h2>
        <p className="oidc-picker-email">{email}</p>
        <p className="oidc-picker-subtitle">
          We found {count} account{count !== 1 ? 's' : ''} associated with this email.
        </p>
      </div>

      <div className="oidc-picker-accounts">
        {existingAccounts.map(account => (
          <button
            key={account.accountLocator}
            className="oidc-picker-account-card"
            onClick={() => onSelectExisting(account)}
            type="button"
          >
            <div className="oidc-picker-account-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 2C7.24 2 5 4.24 5 7c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5z"
                  fill="#29b5e8"
                />
              </svg>
            </div>
            <div className="oidc-picker-account-info">
              <div className="oidc-picker-account-name-row">
                <span className="oidc-picker-account-name">{account.accountName}</span>
                {account.edition && (
                  <span className="oidc-picker-edition-badge">{account.edition}</span>
                )}
              </div>
              <div className="oidc-picker-account-meta">
                <span>{account.regionDisplayName}</span>
                <span className="oidc-picker-meta-separator">·</span>
                <span>Created {formatCreatedAt(account.createdAt)}</span>
              </div>
            </div>
            <div className="oidc-picker-chevron" aria-hidden="true">
              ›
            </div>
          </button>
        ))}

        <button className="oidc-picker-create-new" onClick={onCreateNew} type="button">
          <div className="oidc-picker-create-icon" aria-hidden="true">
            +
          </div>
          <div className="oidc-picker-account-info">
            <div className="oidc-picker-account-name">Create a new account</div>
            <div className="oidc-picker-account-meta">Start a fresh Snowflake trial</div>
          </div>
        </button>
      </div>
    </div>
  );
};
